export const CATEGORIES = [
  "Rifle Scopes",
  "Red Dots & Reflex Sights",
  "Prism Scopes & Magnifiers",
  "Night Vision & Thermal Optics",
  "Lasers",
  "Rangefinders",
  "Binoculars & Spotting Scopes",
  "Mounts, Rings & Bases",
  "Parts & Accessories",
  "Knives & Blades",
  "Services",
];

// The 5 new part parents are TOP-LEVEL categories, peers of the optics above (14 total). Use this
// wherever categories are BROWSED/FILTERED (feed, saved searches, browse tiles, admin filters).
// CATEGORIES above stays the optic-only set (catalog prefill, optic-specific logic).
export const PART_PARENT_CATEGORIES = ["Barrels", "Muzzle Devices", "Chassis", "Uppers", "Sights", "Triggers", "Handguards", "Magazines", "Ammunition", "Drones", "Bags & Packs"];
export const TOP_LEVEL_CATEGORIES = [...CATEGORIES, ...PART_PARENT_CATEGORIES];

export const CONDITIONS = ["New", "Used - Like New", "Used - Good", "Used - Average", "Used - Fair"];

export const LISTING_TYPES = [
  { value: "sell", label: "Want to Sell" },
  { value: "buy", label: "Want to Buy/Trade" },
  { value: "trade", label: "Want to Trade" },
];

// How soon a Want-to-Buy poster needs the item — shown on the WTB detail/card so a seller
// reading the board knows whether it's worth reaching out.
export const WTB_TIMEFRAMES = [
  { value: "asap", label: "ASAP" },
  { value: "2weeks", label: "Within 2 weeks" },
  { value: "month", label: "Within a month" },
  { value: "flexible", label: "No rush / flexible" },
];

// --- Night Vision & Thermal structured fields (2026-06) ----------------
// device_type = FORM FACTOR (mirrors server.py ListingIn.device_type comment); a searchable facet.
// sensor_resolution = free text but suggested from common values; detection_range = yards (number).
export const DEVICE_TYPES = [
  "Weapon Sight / Scope",
  "Clip-On",
  "Monocular / Handheld",
  "Binocular",
  "Scanner",
  "Goggles",
  "Other",
];
export const SENSOR_RESOLUTIONS = [
  "160x120", "256x192", "384x288", "640x480", "640x512", "1024x768", "1024x1024", "1280x1024",
];

export const MAG_RANGES = ["LPVO", "mid-range", "long-range"];
export const RETICLES = ["MOA", "MRAD", "BDC", "N/A"];
export const MOUNTS = ["Picatinny", "Weaver", "Dovetail", "M-LOK", "Cantilever", "Rings included", "RMR footprint", "Other"];
export const TUBE_DIAMETERS = ["1 inch", "30mm", "34mm", "35mm", "Other"];
export const FOCAL_PLANES = ["FFP", "SFP", "N/A"];

// --- Lasers category (2026-06) -----------------------------------------
// IR-capable devices (aiming or illuminator) are export/anti-diversion gated: admin approval +
// used-only condition. Visible-only aiming lasers (red/green/blue) are NOT gated and CAN be New.
// Mirrors server.py IR_LASER_TYPES / IR_LASER_WAVELENGTHS — keep in sync.
export const LASER_TYPES = [
  "Visible Aiming Laser",
  "IR Aiming Laser",
  "IR Illuminator",
  "Dual (Visible + IR)",
  "Combo (Light + Laser)",
];
export const LASER_IR_TYPES = new Set(["IR Aiming Laser", "IR Illuminator", "Dual (Visible + IR)"]);
export const LASER_WAVELENGTHS = [
  "Red (~635 nm)",
  "Green (~520 nm)",
  "Blue (~450 nm)",
  "Near-IR (~850 nm)",
  "Near-IR (~940 nm)",
  "IR (other)",
];
export const LASER_IR_WAVELENGTHS = new Set(["Near-IR (~850 nm)", "Near-IR (~940 nm)", "IR (other)"]);
export const LASER_BATTERIES = ["CR123A", "CR2", "AA", "AAA", "Internal / Rechargeable", "Other"];

// Finish / color options for physical goods (scopes, lasers, mounts, etc.). Feed-filterable.
export const COLORS = ["Black", "FDE", "OD Green", "Tan", "Coyote", "Gray", "Bronze", "Silver / Stainless", "Camo", "Other"];

// --- Knives & Blades category (2026-06) --------------------------------
// Category-conditional structured spec fields (mirror server-side storage in listing.knife_specs).
// Shown/hidden by category exactly like the optic spec fields. All optional — captured, not enforced.
// NOTE: "Automatic/switchblade" may carry state-specific legal restrictions; no enforcement is wired
// today (the seller-declared ITAR flag remains available for anything a seller wants routed to review).
export const KNIVES_CATEGORY = "Knives & Blades";
export const KNIFE_SPECS = [
  { key: "blade_length_in", label: "Blade length (in)", type: "number", hint: "e.g. 3.5" },
  { key: "overall_length_in", label: "Overall length (in)", type: "number", hint: "e.g. 8.0" },
  { key: "blade_steel", label: "Blade steel", type: "text", hint: "e.g. S30V, D2, 154CM" },
  { key: "blade_grind", label: "Blade grind", type: "select", options: ["Flat", "Hollow", "Sabre/Scandi", "Convex", "Chisel"] },
  { key: "edge_type", label: "Edge type", type: "select", options: ["Plain", "Serrated", "Combo"] },
  { key: "point_style", label: "Point / tip style", type: "select", options: ["Drop point", "Clip point", "Tanto", "Spear point", "Sheepsfoot", "Other"] },
  { key: "handle_material", label: "Handle material", type: "text", hint: "e.g. G10, Micarta, Carbon fiber, Wood" },
  { key: "tang", label: "Tang", type: "select", options: ["Full tang", "Partial tang", "Hidden tang"] },
  { key: "locking_mechanism", label: "Locking mechanism", type: "select", options: ["Fixed blade / N/A", "Liner lock", "Frame lock", "Back lock", "Axis/crossbar lock", "Slip joint", "Automatic/switchblade", "Other"] },
  { key: "includes_sheath", label: "Includes sheath / pouch", type: "bool" },
];

// True when a laser is IR-capable → export-gated (admin approval + no "New" condition).
export function laserIsIrCapable(form) {
  const t = (form?.laser_type || "").trim();
  const w = (form?.laser_wavelength || "").trim();
  return LASER_IR_TYPES.has(t) || LASER_IR_WAVELENGTHS.has(w);
}

export const BLOCK_REASONS = [
  { id: "harassment", label: "Harassment" },
  { id: "scam", label: "Scam / fraud attempt" },
  { id: "solicitation", label: "Off-platform solicitation" },
  { id: "spam", label: "Spam" },
  { id: "other", label: "Other" },
];

// Human-readable magnification from structured min/max, falling back to the
// legacy free-text field.
export function magLabel(listing) {
  const l = listing || {};
  if (l.mag_min != null && l.mag_max != null) {
    return l.mag_min === l.mag_max ? `${l.mag_min}x` : `${l.mag_min}-${l.mag_max}x`;
  }
  return l.magnification || "";
}
export const SHIPPING = [
  { value: "ship", label: "Ship" },
  { value: "pickup", label: "Local Pickup" },
  { value: "both", label: "Both" },
];

export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY",
];

export const CATEGORY_ICON = {
  "Rifle Scopes": "crosshair",
  "Red Dots & Reflex Sights": "circle-dot",
  "Prism Scopes & Magnifiers": "search",
  "Night Vision & Thermal Optics": "moon",
  "Lasers": "zap",
  "Rangefinders": "ruler",
  "Binoculars & Spotting Scopes": "binoculars",
  "Mounts, Rings & Bases": "wrench",
  "Parts & Accessories": "package",
  "Knives & Blades": "sword",
  "Services": "settings",
  "Muzzle Devices": "wrench",
  "Ammunition": "package",
  "Drones": "plane",
  "Bags & Packs": "backpack",
};

// --- Ammunition category field options (2026-06) -----------------------
// Seeded from the RSR Group ammunition taxonomy sweep. Offered as listing-form options for the
// Ammunition category; sellers may still type a custom value. Grain weight is a free number field
// (+ a separate shot-size list for shotshell) rather than a fixed dropdown, per the sweep's note.
export const AMMO_SUBCATEGORIES = [
  "Handgun Ammunition", "Rifle Ammunition", "Rimfire Ammunition", "Shot Shell Ammunition",
];
export const AMMO_CALIBERS = [
  "10 Gauge", "12 Gauge", "16 Gauge", "20 Gauge", "28 Gauge", "410 Bore",
  "17 HMR", "17 Hornady Mach 2", "17 Hornet", "17 WSM", "22 LR", "22 Short", "22 WMR", "22 WRF", "22 TCM", "22 Hornet",
  "22-250 Remington", "220 Swift", "222 Remington", "223 Remington", "224 Valkyrie", "22 ARC", "22 Creedmoor", "22 Nosler",
  "204 Ruger", "243 Winchester", "6MM ARC", "6MM Creedmoor", "6MM Remington", "6.5 Creedmoor", "6.5 Grendel", "6.5 PRC",
  "6.5x55 Swedish", "6.8 SPC", "6.8 Western", "25-06 Remington", "25 Creedmoor", "257 Roberts", "257 Weatherby Magnum",
  "260 Remington", "264 Winchester", "270 Winchester", "270 WSM", "270 Weatherby Magnum", "7MM-08", "7MM Remington Magnum",
  "7MM PRC", "7MM Weatherby Magnum", "7X57", "280 Remington", "280 Ackley Improved", "284 Winchester",
  "30 Carbine", "30-30 Winchester", "30-06 Springfield", "300 Blackout", "300 Winchester Magnum", "300 WSM", "300 PRC",
  "300 Weatherby Magnum", "308 Winchester", "303 British", "30 Nosler", "7.62x39", "7.62x54R", "7.62 NATO", "5.56 NATO",
  "5.45x39", "5.7x28mm", "8MM Mauser", "8.6 Blackout", "32 Winchester Special", "325 WSM", "338 Lapua", "338 Win Mag",
  "338 ARC", "33 Nosler", "35 Remington", "35 Whelen", "350 Legend", "360 Buckhammer", "375 H&H", "375 Ruger",
  "444 Marlin", "45-70 Government", "450 Bushmaster", "450 Marlin", "458 SOCOM", "458 Winchester", "50 BMG",
  "25 ACP", "32 ACP", "32 H&R", "32 S&W", "32 S&W Long", "380 ACP", "9MM", "9MM Makarov", "357 Magnum", "357 Sig",
  "38 Special", "38 Super", "40 S&W", "10MM", "41 Magnum", "44 Magnum", "44 Special", "44-40 Winchester", "45 ACP",
  "45 Colt", "45 GAP", "454 Casull", "460 S&W", "480 Ruger", "500 S&W", "50 Action Express", "7.62x25 Tokarev",
  "30 Super Carry", "400 Legend", "277 Fury", "7.5x55mm Swiss",
];
export const AMMO_BULLET_TYPES = [
  "Full Metal Jacket", "FMJ Boat Tail", "Total Metal Jacket", "Hollow Point", "Boat Tail Hollow Point",
  "Jacketed Hollow Point", "Jacketed Soft Point", "Soft Point", "Pointed Soft Point", "Ballistic Tip", "Polymer Tip",
  "V-Max", "ELD-X", "ELD Match", "A-Tip", "SST", "InterLock", "AccuBond", "Partition", "Ballistic Silvertip",
  "Open Tip Match", "Sierra MatchKing", "Sierra GameKing", "Bonded", "Copper / Solid Copper", "Lead Round Nose",
  "Semi Wadcutter", "Wadcutter", "Round Nose", "Flat Nose", "Hard Cast", "Gold Dot Hollow Point", "XTP", "Frangible",
  "Buckshot", "Birdshot", "Rifled Slug", "Sabot Slug", "Steel Shot", "Bismuth", "Tungsten / TSS", "Blank", "Other",
];
export const AMMO_SHOT_SIZES = [
  "#12", "#9", "#8.5", "#8", "#7.5", "#7", "#6", "#5", "#4", "#3", "#2", "BB", "BBB", "T",
  "#4 Buck", "#3 Buck", "#2 Buck", "#1 Buck", "0 Buck", "00 Buck", "000 Buck",
];

export function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

// Human-readable listing URL: /listing/<seller_username>/<seller_seq> when both are present
// (only enriched listing payloads carry seller_seq), else the legacy /listing/<listing_id>.
// The backend resolves BOTH forms (former handles included) and the detail page canonicalizes
// legacy/renamed URLs forward, so this is always safe to call on any object with a listing_id.
export function listingPath(listing) {
  if (!listing) return "/feed";
  const u = listing.seller_username;
  const seq = listing.seller_seq;
  if (u && seq !== undefined && seq !== null && seq !== "") return `/listing/${u}/${seq}`;
  return `/listing/${listing.listing_id}`;
}
